import { ILineItem, IMatchedOrder } from "../types/claim";

/**
 * Patient responsibility extracted from 835 claim adjustments
 * Amounts are in dollars (from 835 EDI data)
 */
export interface IClaimPatientResponsibility {
  deductible: number; // PR adjustment code 1
  coinsurance: number; // PR adjustment code 2
  copay: number; // PR adjustment code 3
  totalOOP: number; // Sum of all PR adjustments
}

/**
 * Extract patient responsibility amounts from underpayment lineItems
 * 835 adjustment codes: 1=Deductible, 2=Coinsurance, 3=Copay
 * Only PR (Patient Responsibility) group adjustments are included
 *
 * @param lineItems - Array of line items from the underpayment
 * @returns Patient responsibility breakdown with amounts in dollars
 */
export function extractClaimPatientResponsibility(
  lineItems?: ILineItem[]
): IClaimPatientResponsibility {
  // Extract all PR adjustments from all line items
  const prAdjustments = (lineItems || []).flatMap((line) =>
    (line.adjustments || []).filter((adj) => adj.group === "PR")
  );

  // Sum amounts by code
  const deductible = prAdjustments
    .filter((adj) => adj.code === "1")
    .reduce((sum, adj) => sum + (adj.amount || 0), 0);
  const coinsurance = prAdjustments
    .filter((adj) => adj.code === "2")
    .reduce((sum, adj) => sum + (adj.amount || 0), 0);
  const copay = prAdjustments
    .filter((adj) => adj.code === "3")
    .reduce((sum, adj) => sum + (adj.amount || 0), 0);

  return {
    deductible,
    coinsurance,
    copay,
    totalOOP: deductible + coinsurance + copay,
  };
}

/**
 * Calculate OOP percentage
 * Formula: patientOOP / (allowedAmount - deductible) * 100
 *
 * @param patientOOP - Total patient out-of-pocket in cents
 * @param allowedAmount - Total allowed amount in cents
 * @param deductible - Patient deductible in cents
 * @returns OOP percentage or null if calculation not possible
 */
export function calculateOOPPercent(
  patientOOP: number,
  allowedAmount: number,
  deductible: number
): number | null {
  const denominator = allowedAmount - deductible;
  if (denominator <= 0) return null;
  return (patientOOP / denominator) * 100;
}

/**
 * Format currency amount from cents to dollars string
 * @param amountInCents - Amount in cents
 * @returns Formatted currency string (e.g., "$1,234.56")
 */
export function formatCurrency(
  amountInCents: number | null | undefined
): string {
  if (amountInCents === null || amountInCents === undefined) {
    return "-";
  }
  const dollars = amountInCents / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(dollars);
}

/**
 * Format percentage value
 * @param percent - Percentage value
 * @returns Formatted percentage string (e.g., "12.5%")
 */
export function formatPercent(percent: number | null | undefined): string {
  if (percent === null || percent === undefined) {
    return "-";
  }
  return `${percent.toFixed(1)}%`;
}

/**
 * Format dollar amount (no conversion needed)
 * Use this for 835 EDI data which is already in dollars
 * @param amountInDollars - Amount in dollars
 * @returns Formatted currency string (e.g., "$1,234.56")
 */
export function formatDollars(
  amountInDollars: number | null | undefined
): string {
  if (amountInDollars === null || amountInDollars === undefined) {
    return "-";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amountInDollars);
}

/**
 * Check if a CPT code is an implant (C1xxx-C9xxx)
 */
export function isImplantCptCode(cptCode: string | null | undefined): boolean {
  if (!cptCode) return false;
  return /^C[1-9]\d{3}$/i.test(cptCode);
}

/**
 * Generate case signature from estimate procedures
 * Matches the backend logic: aggregates CPT codes with quantities, excludes implants,
 * sorts alphabetically, format: "CPT1:QTY1,CPT2:QTY2,..."
 *
 * Only includes FACILITY procedures to match 835 claim case signatures
 */
export function generateEstimateCaseSignature(
  order: IMatchedOrder | null | undefined
): string {
  if (!order) return "";

  const pricingBreakdown = order.gfeQuote?.pricingBreakdown as any;
  if (!pricingBreakdown?.procedureBreakdown?.length) return "";

  // Filter to FACILITY procedures, exclude implants, aggregate quantities by CPT
  const facilityProcedures = pricingBreakdown.procedureBreakdown.filter(
    (proc: any) => proc.specialty === "FACILITY" && !isImplantCptCode(proc.code)
  );

  // Aggregate quantities by CPT code
  const cptQuantities = facilityProcedures.reduce(
    (acc: Record<string, number>, proc: any) => {
      const cptCode = proc.code;
      acc[cptCode] = (acc[cptCode] || 0) + 1;
      return acc;
    },
    {}
  );

  // Convert to array, filter zeros, sort alphabetically, format as CPT:QTY pairs
  return Object.entries(cptQuantities)
    .filter(([, qty]) => qty !== 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([cpt, qty]) => `${cpt}:${qty}`)
    .join(",");
}
